'use client';

import { ButtonHTMLAttributes, CSSProperties, ForwardedRef, ReactNode } from 'react';

import { Styles, css, cva, cx } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

import { IsomorphicLink, IsomorphicLinkProps } from '../../design-utils/isomorphic-link';
import { hexToRgb } from '../../misc/color/rgb';
import { useButtonLoadingState } from '../_button-shared';

const toKeyColorStyle = (keyColor: string): CSSProperties =>
  ({ '--key-color': keyColor, '--key-color-rgb': hexToRgb(keyColor) }) as CSSProperties;

interface SpecialBaseProps {
  hierarchy?: 'primary' | 'secondary';
  label?: ReactNode;
  css?: Styles;
  keyColor?: string;
}

type SpecialButtonElementProps = SpecialBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof SpecialBaseProps | 'className' | 'children' | 'type'> & {
    ref?: ForwardedRef<HTMLButtonElement>;
    /** HTML button type attribute. Use 'submit' for form submission. */
    htmlType?: 'button' | 'submit' | 'reset';
  };

type SpecialLinkElementProps = SpecialBaseProps &
  Omit<IsomorphicLinkProps, keyof SpecialBaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLAnchorElement>;
  };

export type SpecialButtonProps<T extends 'button' | 'a' | undefined = 'button'> = { as?: T } & (T extends 'button'
  ? SpecialButtonElementProps
  : SpecialLinkElementProps);

const SpecialButtonComponent = ({
  hierarchy,
  label,
  css: cssProps,
  keyColor,
  disabled,
  onClick,
  ref,
  htmlType = 'button',
  style,
  ...htmlProps
}: SpecialButtonElementProps) => {
  const { waiting, handleClick } = useButtonLoadingState(onClick);

  return (
    <button
      ref={ref}
      type={htmlType}
      role='button'
      className={cx(specialButtonRecipe({ hierarchy }), css(cssProps))}
      style={keyColor ? { ...toKeyColorStyle(keyColor), ...style } : style}
      disabled={waiting || disabled}
      onClick={handleClick}
      {...htmlProps}
    >
      {label && <span>{label}</span>}
    </button>
  );
};

const SpecialLinkComponent = ({
  hierarchy,
  label,
  keyColor,
  ref,
  css: cssProps,
  style,
  ...htmlProps
}: SpecialLinkElementProps) => {
  return (
    <IsomorphicLink
      ref={ref}
      className={cx(specialButtonRecipe({ hierarchy }), css(cssProps))}
      style={keyColor ? { ...toKeyColorStyle(keyColor), ...style } : style}
      {...htmlProps}
    >
      {label && <span>{label}</span>}
    </IsomorphicLink>
  );
};

export const SpecialButton = <T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  ...htmlProps
}: SpecialButtonProps<T>) => {
  if (as === 'button') {
    return <SpecialButtonComponent {...(htmlProps as SpecialButtonElementProps)} />;
  }
  if (as === 'a') {
    return <SpecialLinkComponent {...(htmlProps as SpecialLinkElementProps)} />;
  }
  return null;
};
SpecialButton.displayName = 'SpecialButton';

export const specialButtonRecipe = cva({
  base: center.raw({
    pos: 'relative',
    h: 54,
    px: 12,
    gap: 4,
    rounded: 1000,
    textStyle: 'Title/XS/Monospace',
    color: 'inverse.white',
    overflow: 'hidden',
    _disabled: {
      cursor: 'not-allowed',
      bg: 'background.default.level-1',
      border: 'none',
      color: 'content.default.level-1',
      boxShadow: 'none',
      _before: { animation: 'none', opacity: 0 },
      _after: { bg: 'background.default.level-1' },
    },
    _focusVisible: {
      outline: '2px solid',
      outlineOffset: 0,
      outlineColor: 'border.default.level-4',
    },
    '& > span': {
      zIndex: 10,
    },
  }),
  defaultVariants: {
    hierarchy: 'primary',
  },
  variants: {
    hierarchy: {
      primary: {
        boxShadow: '0 8px 24px rgba(var(--key-color-rgb), 0.28)',
        _before: {
          content: '""',
          pos: 'absolute',
          zIndex: 0,
          left: '50%',
          top: '50%',
          marginLeft: '-50%',
          marginTop: '-50%',
          w: '100%',
          aspectRatio: '1 / 1',
          bg: 'var(--key-color)',
          bgImage: 'linear-gradient(var(--key-color) 40%, #fff 90%, #fff)',
          bgRepeat: 'no-repeat',
          animation: 'buttonBorderRotate 2s linear infinite',
        },
        _after: {
          content: '""',
          pos: 'absolute',
          zIndex: 5,
          left: 2,
          top: 2,
          w: 'calc(100% - 4px)',
          h: 'calc(100% - 4px)',
          bg: 'var(--key-color)',
          rounded: 1000,
        },
        _hover: {
          _after: {
            bg: 'linear-gradient(rgba(0, 0, 0, 0.20)), linear-gradient(var(--key-color))',
          },
        },
      },
      secondary: {
        bg: 'rgba(var(--key-color-rgb), 0.3)',
        border: '2px solid rgba(var(--key-color-rgb), 0.4)',
        backdropFilter: 'blur(40px)',
        _hover: {
          bg: 'rgba(var(--key-color-rgb), 0.4)',
        },
      },
    },
  },
});
